import {
  REQUEST_CREATE_SURVEY,
  REQUEST_DELETE_SURVEY,
  REQUEST_FETCH_SURVEYS,
  REQUEST_FETCH_SURVEY_RESULT_CATEGORY_DETAIL,
  REQUEST_FETCH_SURVEY_RESULT_CATEGORY_SUMMARY,
  REQUEST_FETCH_SURVEY_RESULT_RATIO_DEPARTMENTS,
  REQUEST_FETCH_SURVEY_RESULT_RATIO_DISTRIBUTION,
  REQUEST_FETCH_SURVEY_RESULT_RATIO_ENGAGEMENTS,
  REQUEST_FETCH_SURVEY_RESULT_RATIO_SUMMARY,
  REQUEST_FETCH_SURVEY_RESULT_RATIO_TIME_SERIES,
  REQUEST_UPDATE_SURVEY,
  SET_SURVEY_ID,
  SUCCESS_FETCH_COMPANY,
} from 'actions/actionTypes';
import * as AppAction from 'actions/appActions';
import * as Action from 'actions/backend/engagementSurvey/resultActions';
import * as FlashMessageAction from 'actions/flashMessageActions';
import * as API from 'apis/backend';
import { QUESTION_CATEGORY_TYPES } from 'constants/surveys';
import { all, call, fork, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import {
  QuestionCategoryTypes,
  RequestFetchSurveyResultCategoryDetailTypes,
  RequestFetchSurveyResultRatioDepartmentsTypes,
  SurveyIdTypes,
  SurveyInputTypes,
  SurveyTypes,
} from 'types/backend/engagementSurvey/result';
import { navigate } from 'utils/navigation';

function* runRequestFetchSurveys() {
  const { rootPath } = yield select((state) => state.app);
  const { currentCompany } = yield select((state) => state.backend.company);
  const { payload, error, newXCSRFToken } = yield call(
    API.requestFetchSurveys,
    rootPath,
    currentCompany,
  );

  if (payload && !error) {
    yield all([
      put(AppAction.setXCSRFToken(newXCSRFToken)),
      put(Action.successFetchSurveys(payload)),
    ]);
  } else {
    yield put(Action.failureFetchSurveys());
  }
}

// BackyardRouterがマウントされる毎に「REQUEST_FETCH_SURVEYS」を発火させていたが、「SUCCESS_FETCH_COMPANY」より前に診断取得が完了すると企業切り替えがうまくいかない為。
function* handleRequestFetchSurveysInBackyard() {
  while (true) {
    yield take(SUCCESS_FETCH_COMPANY);
    yield fork(runRequestFetchSurveys);
  }
}

function* handleRequestFetchSurveysInAdmin() {
  while (true) {
    yield take(REQUEST_FETCH_SURVEYS);
    yield fork(runRequestFetchSurveys);
  }
}

function* runRequestCreateSurvey(params: SurveyInputTypes) {
  const { rootPath, xCSRFToken } = yield select((state) => state.app);
  const { currentCompany } = yield select((state) => state.backend.company);
  yield put(
    FlashMessageAction.addFlashMessage({
      type: 'success',
      message: 'エンゲージメント診断を作成中です。',
    }),
  );
  const { payload, error, newXCSRFToken } = yield call(
    API.requestCreateSurvey,
    rootPath,
    currentCompany,
    params,
    xCSRFToken,
  );
  if (payload && !error) {
    yield all([
      put(AppAction.setXCSRFToken(newXCSRFToken)),
      put(Action.successCreateSurvey(payload)),
      put(
        FlashMessageAction.addFlashMessage({
          type: 'success',
          message: 'エンゲージメント診断を作成しました。',
        }),
      ),
    ]);
  } else {
    yield all([
      put(Action.failureCreateSurvey()),
      put(
        FlashMessageAction.addFlashMessage({
          type: 'error',
          message: 'エンゲージメント診断の作成に失敗しました。',
        }),
      ),
    ]);
  }
}

function* handleRequestCreateSurvey() {
  while (true) {
    const { params } = yield take(REQUEST_CREATE_SURVEY);
    yield fork(runRequestCreateSurvey, params);
  }
}

function* runRequestUpdateSurvey(params: SurveyInputTypes) {
  const { rootPath, xCSRFToken } = yield select((state) => state.app);
  const { currentCompany } = yield select((state) => state.backend.company);
  const { payload, error, newXCSRFToken } = yield call(
    API.requestUpdateSurvey,
    rootPath,
    currentCompany,
    params,
    xCSRFToken,
  );

  if (payload && !error) {
    yield all([
      put(AppAction.setXCSRFToken(newXCSRFToken)),
      put(Action.successUpdateSurvey(payload)),
      put(
        FlashMessageAction.addFlashMessage({
          type: 'success',
          message: 'エンゲージメント診断を更新しました。',
        }),
      ),
    ]);
  } else {
    yield all([
      put(Action.failureUpdateSurvey()),
      put(
        FlashMessageAction.addFlashMessage({
          type: 'error',
          message: 'エンゲージメント診断の更新に失敗しました。',
        }),
      ),
    ]);
  }
}

function* handleRequestUpdateSurvey() {
  while (true) {
    const { params } = yield take(REQUEST_UPDATE_SURVEY);
    yield fork(runRequestUpdateSurvey, params);
  }
}

function* runRequestDeleteSurvey(id: string) {
  const { rootPath, xCSRFToken } = yield select((state) => state.app);
  const { currentCompany } = yield select((state) => state.backend.company);
  const { payload, error, newXCSRFToken } = yield call(
    API.requestDeleteSurvey,
    rootPath,
    currentCompany,
    id,
    xCSRFToken,
  );

  if (payload === 'success' && !error) {
    yield all([
      put(AppAction.setXCSRFToken(newXCSRFToken)),
      put(Action.successDeleteSurvey(id)),
      put(
        FlashMessageAction.addFlashMessage({
          type: 'success',
          message: 'エンゲージメント診断を削除しました。',
        }),
      ),
    ]);
    if (rootPath === 'admin') {
      yield call(navigate, '/admin/surveys');
    } else {
      yield call(navigate, `/backyard/companies/${currentCompany.id}/surveys`);
    }
  } else {
    yield all([
      put(Action.failureDeleteSurvey()),
      put(
        FlashMessageAction.addFlashMessage({
          type: 'error',
          message: 'エンゲージメント診断の削除に失敗しました。',
        }),
      ),
    ]);
    if (rootPath === 'admin') {
      yield call(navigate, '/admin/surveys');
    } else {
      yield call(navigate, `/backyard/companies/${currentCompany.id}/surveys`);
    }
  }
}

function* handleRequestDeleteSurvey() {
  while (true) {
    const { id } = yield take(REQUEST_DELETE_SURVEY);
    yield fork(runRequestDeleteSurvey, id);
  }
}

function* runSetSurveyId(id: string) {
  const { rootPath } = yield select((state) => state.app);
  const { visibleResultSurveys } = yield select((state) => state.backend.engagementSurvey.result);

  const [currentSurvey] = visibleResultSurveys.filter((survey: SurveyTypes) => survey.id === id);

  if (!currentSurvey) {
    if (rootPath === 'admin') {
      yield call(navigate, '/admin/no_results');
    } else {
      yield call(navigate, '/backyard/no_results');
    }
  } else {
    yield put(
      FlashMessageAction.addFlashMessage({
        type: 'success',
        message: `${currentSurvey.startAt} のレポートをセットしました。`,
      }),
    );
  }
}

function* handleSetSurveyId() {
  while (true) {
    const { id } = yield take(SET_SURVEY_ID);
    yield fork(runSetSurveyId, id);
  }
}

function* runRequestFetchSurveyResultRatioSummary(surveyId: SurveyIdTypes) {
  const { rootPath } = yield select((state) => state.app);
  const { currentCompany } = yield select((state) => state.backend.company);
  const { payload, error, newXCSRFToken } = yield call(
    API.requestFetchSurveyResultRatioSummary,
    rootPath,
    surveyId,
    currentCompany,
  );

  if (payload && !error) {
    yield all([
      put(AppAction.setXCSRFToken(newXCSRFToken)),
      put(Action.successFetchSurveyResultRatioSummary(payload)),
    ]);
  } else {
    yield put(Action.failureFetchSurveyResultRatioSummary());
  }
}

function* handleRequestFetchSurveyResultRatioSummary() {
  while (true) {
    const { surveyId } = yield take(REQUEST_FETCH_SURVEY_RESULT_RATIO_SUMMARY);
    yield fork(runRequestFetchSurveyResultRatioSummary, surveyId);
  }
}

function* runRequestFetchReportRatioDistribution(surveyId: SurveyIdTypes) {
  const { rootPath } = yield select((state) => state.app);
  const { currentCompany } = yield select((state) => state.backend.company);
  const { payload, error, newXCSRFToken } = yield call(
    API.requestFetchSurveyResultRatioDistribution,
    rootPath,
    surveyId,
    currentCompany,
  );

  if (payload && !error) {
    yield all([
      put(AppAction.setXCSRFToken(newXCSRFToken)),
      put(Action.successFetchSurveyResultRatioDistribution(payload)),
    ]);
  } else {
    yield put(Action.failureFetchSurveyResultRatioDistribution());
  }
}

function* handleRequestFetchSurveyResultRatioDistribution() {
  while (true) {
    const { surveyId } = yield take(REQUEST_FETCH_SURVEY_RESULT_RATIO_DISTRIBUTION);
    yield fork(runRequestFetchReportRatioDistribution, surveyId);
  }
}

function* runRequestFetchSurveyResultRatioEngagements(surveyId: SurveyIdTypes) {
  const { rootPath } = yield select((state) => state.app);
  const { currentCompany } = yield select((state) => state.backend.company);
  const { payload, error, newXCSRFToken } = yield call(
    API.requestFetchSurveyResultRatioEngagements,
    rootPath,
    surveyId,
    currentCompany,
  );

  if (payload && !error) {
    yield all([
      put(AppAction.setXCSRFToken(newXCSRFToken)),
      put(Action.successFetchSurveyResultRatioEngagements(payload)),
    ]);
  } else {
    yield put(Action.failureFetchSurveyResultRatioEngagements());
  }
}

function* handleRequestFetchSurveyResultRatioEngagements() {
  while (true) {
    const { surveyId } = yield take(REQUEST_FETCH_SURVEY_RESULT_RATIO_ENGAGEMENTS);
    yield fork(runRequestFetchSurveyResultRatioEngagements, surveyId);
  }
}

function* runRequestFetchSurveyResultRatioTimeSeries(surveyId: SurveyIdTypes) {
  const { rootPath } = yield select((state) => state.app);
  const { currentCompany } = yield select((state) => state.backend.company);
  const { payload, error, newXCSRFToken } = yield call(
    API.requestFetchSurveyResultRatioTimeSeries,
    rootPath,
    surveyId,
    currentCompany,
  );

  if (payload && !error) {
    yield all([
      put(AppAction.setXCSRFToken(newXCSRFToken)),
      put(Action.successFetchSurveyResultRatioTimeSeries(payload)),
    ]);
  } else {
    yield put(Action.failureFetchSurveyResultRatioTimeSeries());
  }
}

function* handleRequestFetchSurveyResultRatioTimeSeries() {
  while (true) {
    const { surveyId } = yield take(REQUEST_FETCH_SURVEY_RESULT_RATIO_TIME_SERIES);
    yield fork(runRequestFetchSurveyResultRatioTimeSeries, surveyId);
  }
}

function* runRequestFetchSurveyResultRatioDepartments(
  action: RequestFetchSurveyResultRatioDepartmentsTypes,
) {
  const { surveyId, parentId } = action;
  const { rootPath } = yield select((state) => state.app);
  const { currentCompany } = yield select((state) => state.backend.company);
  const { payload, error, newXCSRFToken } = yield call(
    API.requestFetchSurveyResultRatioDepartments,
    rootPath,
    surveyId,
    parentId,
    currentCompany,
  );

  if (payload && !error) {
    yield all([
      put(AppAction.setXCSRFToken(newXCSRFToken)),
      put(Action.successFetchSurveyResultRatioDepartments(payload)),
    ]);
  } else {
    yield put(Action.failureFetchSurveyResultRatioDepartments());
  }
}

function* handleRequestFetchSurveyResultRatioDepartments() {
  yield takeLatest(
    REQUEST_FETCH_SURVEY_RESULT_RATIO_DEPARTMENTS,
    runRequestFetchSurveyResultRatioDepartments,
  );
}

function* runRequestFetchSurveyResultCategorySummary(surveyId: SurveyIdTypes) {
  const { rootPath } = yield select((state) => state.app);
  const { currentCompany } = yield select((state) => state.backend.company);
  const { payload, error, newXCSRFToken } = yield call(
    API.requestFetchSurveyResultCategorySummary,
    rootPath,
    surveyId,
    currentCompany,
  );

  if (payload && !error) {
    yield all([
      put(AppAction.setXCSRFToken(newXCSRFToken)),
      put(Action.successFetchSurveyResultCategorySummary(payload)),
    ]);
  } else {
    yield put(Action.failureFetchSurveyResultCategorySummary());
  }
  // 負荷分散の為にサマリー取得後に詳細をリクエスト
  yield all(
    QUESTION_CATEGORY_TYPES.map((categoryType: QuestionCategoryTypes) => {
      return put(Action.requestFetchSurveyResultCategoryDetail(categoryType, surveyId));
    }),
  );
}

function* handleRequestFetchSurveyResultCategorySummary() {
  while (true) {
    const { surveyId } = yield take(REQUEST_FETCH_SURVEY_RESULT_CATEGORY_SUMMARY);
    yield fork(runRequestFetchSurveyResultCategorySummary, surveyId);
  }
}

function* runRequestFetchSurveyResultCategoryDetail(
  action: RequestFetchSurveyResultCategoryDetailTypes,
) {
  const { rootPath } = yield select((state) => state.app);
  const { currentCompany } = yield select((state) => state.backend.company);
  const { categoryType, surveyId } = action;
  const { payload, error, newXCSRFToken } = yield call(
    API.requestFetchSurveyResultCategoryDetail,
    rootPath,
    categoryType,
    surveyId,
    currentCompany,
  );

  if (payload && !error) {
    yield all([
      put(AppAction.setXCSRFToken(newXCSRFToken)),
      put(Action.successFetchSurveyResultCategoryDetail(categoryType, payload)),
    ]);
  } else {
    yield put(Action.failureFetchSurveyResultCategoryDetail(categoryType));
  }
}

function* handleRequestReportQuestionCategoryDetails() {
  yield takeEvery(
    REQUEST_FETCH_SURVEY_RESULT_CATEGORY_DETAIL,
    runRequestFetchSurveyResultCategoryDetail,
  );
}

export default function* surveySaga() {
  yield fork(handleRequestFetchSurveysInBackyard);
  yield fork(handleRequestFetchSurveysInAdmin);
  yield fork(handleRequestCreateSurvey);
  yield fork(handleRequestUpdateSurvey);
  yield fork(handleRequestDeleteSurvey);
  yield fork(handleSetSurveyId);
  yield fork(handleRequestFetchSurveyResultRatioSummary);
  yield fork(handleRequestFetchSurveyResultRatioDistribution);
  yield fork(handleRequestFetchSurveyResultRatioEngagements);
  yield fork(handleRequestFetchSurveyResultRatioTimeSeries);
  yield fork(handleRequestFetchSurveyResultRatioDepartments);
  yield fork(handleRequestFetchSurveyResultCategorySummary);
  yield fork(handleRequestReportQuestionCategoryDetails);
}
